<template>
  <section
    :id="data.id"
    class="content-visibility relative xh-[610px] sm:h-[602px] sm:mx-auto p-4 sm:p-6 rounded-none sm:rounded-lg overflow-hidden"
    :style="{ backgroundColor: constantData.style.containerColor }"
  >
    <!-- widget header area -->
    <div class="widget-header w-full relative flex items-center mb-4">
      <div
        class="flex items-center relative w-auto"
        :style="{ backgroundColor: constantData.style.containerColor, color: constantData.style.headerTextColor }"
      >
        <OIcon :icon="`category/${constantData.style.icon}`" svg-class="w-8 h-8" />
        <div class="block pl-1 flex-shrink-0 mr-1" :class="isMobile ? 'text-xl' : 'text-20'">{{ constantData.text.headerName }}</div>
      </div>
      <a
        :href="constantData.text.headerLink"
        class="absolute flex items-center"
        :class="isMobile ? 'right-3' : 'right-5'"
        :style="{ backgroundColor: constantData.style.containerColor, color: constantData.style.linkColor }"
      >
        <div class="pl-1 flex-shrink-0 font-normal" :class="isMobile ? 'text-md' : 'text-base'">{{ constantData.text.headerLinkName }}</div>
        <ORawIcon icon="homepage/arrow-circle-right" svg-class="w-4 h-4 mx-1" />
      </a>
    </div>

    <!-- widget body area -->
    <div class="widget-body flex flex-col sm:flex-row">
      <div class="w-full sm:w-[680px] sm:flex-1 sm:mr-6">
        <!-- video area -->
        <div class="widget-player w-full h-[193px] sm:h-[382px] rounded-md u-center">
          <WidgetPlayer v-if="videoArticleSource === 'onedio'" :article="videoArticle" />
          <WidgetPlayerYoutube v-else-if="videoArticleSource === 'youtube'" :article="videoArticle" />
        </div>
        <!-- title area -->
        <OLink
          :href="articleUrl"
          class="text-white-90 text-20 sm:text-24 mt-4 font-bold leading-24 sm:leading-32 line-clamp-3 sm:line-clamp-2"
        >
          {{ videoArticle.title }}
        </OLink>
        <!-- show article button -->
        <OButton
          id="showArticle"
          :href="articleUrl"
          pure
          class="flex items-center w-max h-8 sm:px-6 my-4 text-13 font-semibold text-white sm:bg-[#306AFF] sm:rounded-full"
          @click="goToArticle"
        >
          <span>{{ $t('main.show-article') }}</span>
          <OIcon icon="right-white" svg-class="w-6 h-6" class="ml-2" svg-style="fill: #fff" />
        </OButton>
      </div>

      <!-- playlist mobile area -->
      <div v-if="isMobile" class="flex flex-wrap gap-2">
        <VideosWidgetCard
          v-for="(article, index) in mobileArticles"
          :key="`videoswidget_${article._id}_${isMobile ? 'mobile' : 'desktop'}`"
          :article="article"
          :item="article"
          :index="index"
          :duration="duration(article)"
          :is-current="activeIndex === index"
          @click="handleClickVideoArticle(index)"
          @view="sendViewItemDatalayer"
        />
        <div v-if="showMore" class="flex items-center flex-grow justify-center cursor-pointer w-full mt-2" @click="showMore = false">
          <div class="text-[#9B9B9B] text-md mx-3 flex items-center">
            <OIcon icon="more-blue" svg-class="text-[#A5B2EC] mr-2" />
            <div class="text-[#A5B2EC] text-md font-bold">{{ $t('main.show-more') }}</div>
          </div>
        </div>
      </div>
      <!-- playlist desktop area -->
      <div v-else class="widget-playlist w-full sm:w-[365px] h-[200px] sm:h-[530px] flex flex-col overflow-hidden">
        <Swiper :settings="settings" :modules="[FreeMode, Mousewheel]" @swiper="onSwiper">
          <SwiperSlide v-for="(item, index) in articles" :key="item._id">
            <VideosWidgetArticle
              :item="item"
              :index="index"
              :active-index="activeIndex"
              @click="handleVideoChange(index)"
              @view="sendViewItemDatalayer"
            />
          </SwiperSlide>
        </Swiper>
      </div>
      <!-- end -->
    </div>
  </section>
</template>

<script>
import { defineComponent, computed, ref, onBeforeUnmount } from '@nuxtjs/composition-api';
import Swiper from '@/modules/sliders/core/Swiper';
import SwiperSlide from '@/modules/sliders/core/SwiperSlide';
import { FreeMode, Mousewheel } from 'swiper';
import WidgetPlayer from './WidgetPlayer.vue';
import WidgetPlayerYoutube from './WidgetPlayerYoutube.vue';
import VideosWidgetArticle from './VideosWidgetArticle.vue';
import { useDevice } from '~/composables/useDevice';
import { OIcon, OImage, OButton, OLink, ORawIcon } from '~/modules/common/components/ui';
import { useTracking } from '~/modules/tracking/composables/useTracking';
import { getArticleUrl } from '~/modules/common/utils/routingUtils';
import { getFormattedTime } from '~/modules/common/utils/dateUtils';
import { bodyClassFixer } from '~/modules/common/utils/helpers';
import VideosWidgetCard from '~/modules/card/templates/homepage/VideosWidgetCard.vue';
import { eventBus } from '~/modules/common/utils/eventBus';

export default defineComponent({
  props: {
    data: { type: Object },
  },
  components: {
    OButton,
    OImage,
    OLink,
    OIcon,
    ORawIcon,
    Swiper,
    SwiperSlide,
    WidgetPlayer,
    VideosWidgetCard,
    WidgetPlayerYoutube,
    VideosWidgetArticle,
  },
  setup(props) {
    // TODO: videojs lib brokes body classes. this solve is experimental
    bodyClassFixer();

    const { isMobile } = useDevice();
    const activeIndex = ref(0);
    const articles = ref(
      props.data.data?.filter(
        (article) => article.videoEntry !== undefined && ['onedio', 'youtube'].includes(article.videoEntry.videoData?.source)
      )
    );
    const showMore = ref(true);
    const mobileArticles = computed(() => (showMore.value ? articles.value.slice(0, 6) : articles.value));
    const playingInteraction = ref(true);
    const handleClickVideoArticle = (index) => {
      // pause video if currently playing
      if (activeIndex.value === index) {
        if (playingInteraction.value) {
          eventBus.emit('widget-player-playing-trigger', false);
          playingInteraction.value = false;
        } else {
          eventBus.emit('widget-player-playing-trigger', true);
          playingInteraction.value = true;
        }
        return;
      }
      playingInteraction.value = true;
      // change video
      handleVideoChange(index);
      const targetSection = document.getElementById('videos');
      if (!targetSection) return;
      window.scrollTo({
        top: targetSection.offsetTop - (isMobile.value ? 56 : 0),
        behavior: 'smooth',
      });
      setTimeout(() => {
        sendVideoWidgetEvent('click', [articles.value[activeIndex.value]], activeIndex.value + 1);
      }, 100);
    };

    // Temporary data. When data will come from backend, these constants will be changed
    const constantData = {
      style: {
        icon: 'video-blue',
        containerColor: '#080C25',
        arrowColor: '#080C25',
        linkColor: '#D0D9FF',
        headerTextColor: '#3071E7',
      },
      text: {
        headerName: props.data.title,
        headerLinkName: 'Tümünü Gör',
        headerLink: props.data.href,
      },
    };

    const handleVideoChange = (index) => {
      setTimeout(() => {
        sendVideoWidgetEvent('view_item_list', [articles.value[activeIndex.value]], activeIndex.value + 1);
      }, 1000);
      activeIndex.value = index;
    };

    const articleUrl = computed(() => {
      const article = articles.value[activeIndex.value];
      return getArticleUrl({ article });
    });

    const videoArticle = computed(() => {
      const article = articles.value[activeIndex.value];
      return article;
    });

    const videoArticleSource = computed(() => articles.value[activeIndex.value].videoEntry?.videoData?.source);

    const duration = (article) => getFormattedTime(article.videoEntry.videoData.duration);

    // Slider ---------------------------
    const settings = computed(() => ({
      direction: 'vertical',
      slidesPerView: isMobile.value ? 2.5 : 6.3,
      slideToClickedSlide: true,
      speed: 150,
      loop: false,
      preloadImages: false,
      mousewheel: true,
      freeMode: true,
    }));

    // Data Layer Impression & Click
    const { sendVideoWidgetEvent } = useTracking();

    const handleGotoNextVideo = () => {
      const index = activeIndex.value + 1;
      if ((isMobile.value && index < mobileArticles.value.length) || (!isMobile.value && index < articles.value.length)) {
        handleClickVideoArticle(index);
      }
    };

    eventBus.on('widget-player-video-ended', handleGotoNextVideo);

    onBeforeUnmount(() => {
      eventBus.off('widget-player-video-ended', handleGotoNextVideo);
    });

    const onSwiper = (swiper) => {
      swiper.on('click', function () {
        setTimeout(() => {
          sendVideoWidgetEvent('click', [articles.value[activeIndex.value]], activeIndex.value + 1);
        }, 100);
      });
    };

    const goToArticle = () => {
      sendVideoWidgetEvent('select_item', [articles.value[activeIndex.value]], activeIndex.value + 1);
    };

    // datalayer ---------
    const datalayerArticles = ref([]);
    const shouldSendList = ref(true);
    const sendDatalayerList = () => {
      if (shouldSendList) {
        shouldSendList.value = false;
        setTimeout(() => {
          if (datalayerArticles.value.length > 0) {
            sendVideoWidgetEvent(
              'view',
              datalayerArticles.value.sort((a, b) => a.position - b.position),
              1
            );
            datalayerArticles.value = [];
            shouldSendList.value = true;
          }
        }, 1000);
      }
    };

    const sendViewItemDatalayer = ({ item, position }) => {
      item.position = position;
      datalayerArticles.value.push(item);
      sendDatalayerList();
    };

    return {
      constantData,
      isMobile,
      articles,
      settings,
      onSwiper,
      activeIndex,
      handleVideoChange,
      sendViewItemDatalayer,
      articleUrl,
      videoArticle,
      videoArticleSource,
      duration,
      showMore,
      goToArticle,
      mobileArticles,
      handleClickVideoArticle,
      FreeMode,
      Mousewheel,
    };
  },
});
</script>
<style scoped lang="scss">
.widget-header::before {
  content: '';
  position: absolute;
  width: 100%;
  top: 50%;
  transform: translate(0, -50%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.22);
}

.widget-playlist {
  .swiper-slide {
    width: 100% !important;
  }
}

.content-visibility {
  content-visibility: auto;
  contain-intrinsic-size: 976px;
  @include sm() {
    content-visibility: visible;
    contain-intrinsic-size: 602px;
  }
}
</style>
